import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  FileUp,
  Upload,
  CheckCircle,
  Clock,
  Loader2,
  FileText,
  AlertTriangle,
} from 'lucide-react';

const DocumentUpload = () => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  
  // Upload dialog
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/document-campaigns/active');
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to load document requests');
    } finally {
      setLoading(false);
    }
  };

  const openUploadDialog = async (campaign) => {
    setSelectedCampaign(campaign);
    setUploadedFiles({});
    setUploadDialog(true);
    
    // Initialize or get existing submission
    if (!campaign.has_submitted) {
      try {
        const response = await api.post(`/document-campaigns/campaigns/${campaign.id}/submit`);
        setSubmissionId(response.data.submission_id);
      } catch (error) {
        toast.error('Failed to initialize submission');
      }
    } else if (campaign.submission) {
      setSubmissionId(campaign.submission.id);
      // Pre-populate uploaded files
      const files = {};
      campaign.submission.documents?.forEach(doc => {
        files[doc.document_type_id] = {
          name: doc.original_filename,
          uploaded: true,
          fileId: doc.id
        };
      });
      setUploadedFiles(files);
    }
  };

  const handleFileSelect = async (documentTypeId, file) => {
    if (!file) return;
    if (!submissionId) {
      toast.error('Submission not initialized');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(prev => ({ ...prev, [documentTypeId]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(
        `/document-campaigns/submissions/${submissionId}/upload/${documentTypeId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setUploadedFiles(prev => ({
        ...prev,
        [documentTypeId]: { name: file.name, uploaded: true }
      }));
      toast.success(`${file.name} uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(prev => ({ ...prev, [documentTypeId]: false }));
    }
  };

  const completeSubmission = async () => {
    if (!submissionId) return;

    // Check required documents
    const requiredDocs = selectedCampaign.document_types?.filter(dt => dt.required) || [];
    const missingDocs = requiredDocs.filter(dt => !uploadedFiles[dt.id]?.uploaded);
    
    if (missingDocs.length > 0) {
      toast.error(`Please upload required documents: ${missingDocs.map(d => d.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/document-campaigns/submissions/${submissionId}/complete`);
      toast.success('Documents submitted successfully!');
      setUploadDialog(false);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Document Requests">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingCampaigns = campaigns.filter(c => !c.has_submitted);
  const completedCampaigns = campaigns.filter(c => c.has_submitted);

  return (
    <DashboardLayout title="Document Requests">
      <div className="space-y-5 lg:space-y-6" data-testid="document-upload-page">
        {/* Header */}
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-slate-800">Document Requests</h2>
          <p className="text-sm lg:text-base text-slate-500">Upload requested documents for the academy</p>
        </div>

        {/* Pending Requests */}
        {pendingCampaigns.length > 0 && (
          <div>
            <h3 className="text-base lg:text-lg font-semibold mb-2.5 lg:mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 lg:h-5 lg:w-5 text-amber-500" />
              Pending Requests ({pendingCampaigns.length})
            </h3>
            <div className="grid gap-3 lg:gap-4 grid-cols-1 md:grid-cols-2">
              {pendingCampaigns.map(campaign => (
                <Card key={campaign.id} className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {campaign.title}
                      <Badge className="bg-amber-100 text-amber-700">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    </CardTitle>
                    {campaign.description && (
                      <CardDescription>{campaign.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {campaign.document_types?.map((dt, idx) => (
                          <Badge key={idx} variant="outline" className="bg-white">
                            <FileText className="h-3 w-3 mr-1" />
                            {dt.label}
                            {dt.required && <span className="text-red-500 ml-1">*</span>}
                          </Badge>
                        ))}
                      </div>
                      
                      {campaign.deadline && (
                        <p className="text-xs text-amber-700">
                          Due: {new Date(campaign.deadline).toLocaleDateString()}
                        </p>
                      )}
                      
                      <Button
                        onClick={() => openUploadDialog(campaign)}
                        className="w-full bg-amber-600 hover:bg-amber-700"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Documents
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Completed Requests */}
        {completedCampaigns.length > 0 && (
          <div>
            <h3 className="text-base lg:text-lg font-semibold mb-2.5 lg:mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 lg:h-5 lg:w-5 text-green-500" />
              Completed ({completedCampaigns.length})
            </h3>
            <div className="grid gap-3 lg:gap-4 grid-cols-1 md:grid-cols-2">
              {completedCampaigns.map(campaign => (
                <Card key={campaign.id} className="border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {campaign.title}
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Submitted
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {campaign.submission?.documents?.map((doc, idx) => (
                        <Badge key={idx} variant="outline" className="bg-green-50">
                          <FileText className="h-3 w-3 mr-1" />
                          {doc.original_filename}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Submitted: {campaign.submission?.updated_at 
                        ? new Date(campaign.submission.updated_at).toLocaleDateString()
                        : campaign.submission?.submitted_at 
                        ? new Date(campaign.submission.submitted_at).toLocaleDateString()
                        : 'N/A'}
                    </p>
                    
                    {/* Edit button - only show if campaign is still active */}
                    {campaign.can_edit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openUploadDialog(campaign)}
                        className="mt-3 w-full"
                        data-testid={`edit-submission-${campaign.id}`}
                      >
                        <FileUp className="h-4 w-4 mr-2" />
                        Edit / Replace Documents
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No Requests */}
        {campaigns.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileUp className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Document Requests</h3>
              <p className="text-slate-500 mt-2">You don't have any pending document requests.</p>
            </CardContent>
          </Card>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileUp className="h-5 w-5 text-blue-600" />
                {selectedCampaign?.has_submitted ? 'Edit Documents' : 'Upload Documents'}
              </DialogTitle>
              <DialogDescription>
                {selectedCampaign?.title}
                {selectedCampaign?.has_submitted && (
                  <span className="block text-xs mt-1 text-blue-600">
                    You can replace any previously uploaded documents while the campaign is active.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            {selectedCampaign && (
              <div className="space-y-4 py-4">
                {selectedCampaign.description && (
                  <p className="text-sm text-slate-600 p-3 bg-slate-50 rounded-lg">
                    {selectedCampaign.description}
                  </p>
                )}

                {selectedCampaign.document_types?.map(dt => (
                  <div key={dt.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium">
                        {dt.label}
                        {dt.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {uploadedFiles[dt.id]?.uploaded && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Uploaded
                        </Badge>
                      )}
                    </div>
                    
                    {dt.description && (
                      <p className="text-xs text-slate-500 mb-2">{dt.description}</p>
                    )}

                    {uploadedFiles[dt.id]?.uploaded ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                          <span className="text-sm text-green-700 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            {uploadedFiles[dt.id].name}
                          </span>
                        </div>
                        {/* Replace file option */}
                        <div className="relative">
                          <Input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                            onChange={(e) => handleFileSelect(dt.id, e.target.files[0])}
                            disabled={uploading[dt.id]}
                            className="cursor-pointer text-xs"
                          />
                          {uploading[dt.id] && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded">
                              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-1">Select a new file to replace</p>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                          onChange={(e) => handleFileSelect(dt.id, e.target.files[0])}
                          disabled={uploading[dt.id]}
                          className="cursor-pointer"
                        />
                        {uploading[dt.id] && (
                          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <p className="text-xs text-slate-500">
                  Accepted formats: PDF, PNG, JPG, GIF, WebP (max 10MB each)
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={completeSubmission}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {selectedCampaign?.has_submitted ? 'Save Changes' : 'Submit Documents'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DocumentUpload;
